/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔐 Built-in Roles — seeded into every account on first signup and
/// kept in sync on every login (so newly-added permissions land on the
/// admin role without a manual migration).
///
/// Built-in roles share a few invariants:
///   - Their `id` is stable (`admin`, `editor`, `viewer`, `billing`).
///     Code that checks "is this user an admin?" reads the role doc's
///     `id` rather than its label, since the label is editable in the
///     UI.
///   - They are flagged `isBuiltIn: true` in Firestore. The roles
///     editor renders them as read-only, but admins can clone any
///     built-in role to start a new custom role.
///   - Built-in roles are not deletable. The roles list UI hides the
///     delete action when `isBuiltIn === true`.
///
/// SuperAdmin is intentionally NOT a built-in role docs go in tenant
/// accounts — it lives outside the account-scoped roles collection at
/// `/superAdmins/{uid}`. We expose `SUPER_ADMIN_ROLE` here so the
/// permissionsStore can describe a SuperAdmin's effective permissions
/// using the same shape, but no Firestore doc is ever written for it.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { ALL_ACCOUNT_PERMISSIONS, ALL_PERMISSIONS, expandImplied } from './permissions'

export interface BuiltInRoleDefinition {
  id: string
  name: string
  description: string
  permissions: string[]
  /** Color hint used by the role badge. Free-form Tailwind palette name (purple, blue, gray, emerald, amber). */
  color: string
}

/**
 * Tenant Admin — the role granted to the user who creates the account.
 * Holds every account-scoped permission. Cannot be deleted, cannot be
 * edited (other than cloning).
 */
export const ADMIN_ROLE: BuiltInRoleDefinition = {
  id: 'admin',
  name: 'Admin',
  description:
    'Full administrative control over this account: users, roles, billing, Snowflake configuration, and all data surfaces. Cannot be deleted.',
  permissions: ALL_ACCOUNT_PERMISSIONS,
  color: 'purple',
}

/**
 * Editor — can author and modify most data assets but cannot manage
 * the account itself (no users, no billing, no Snowflake config).
 */
export const EDITOR_ROLE: BuiltInRoleDefinition = {
  id: 'editor',
  name: 'Editor',
  description:
    'Author and modify worksheets, pipelines, workflows, and governance metadata. Cannot manage users, billing, or Snowflake settings.',
  permissions: expandImplied([
    'dashboards:view',
    'cost:view',
    'observability:view',
    'governance:edit',
    'ingest:edit',
    'pipelines:run',
    'worksheets:create',
    'worksheets:edit',
    'worksheets:run',
    'worksheets:delete',
    'dataflow:edit',
    'dataflow:run',
    'credentials:edit',
    'roles:view',
    'users:view',
  ]),
  color: 'blue',
}

/**
 * Viewer — read-only across the dashboards, governance views, and
 * worksheets. Cannot run, modify, or create anything.
 */
export const VIEWER_ROLE: BuiltInRoleDefinition = {
  id: 'viewer',
  name: 'Viewer',
  description:
    'Read-only access to dashboards, governance views, observability, and worksheets. Cannot run queries or modify any settings.',
  permissions: expandImplied([
    'dashboards:view',
    'cost:view',
    'observability:view',
    'governance:view',
    'ingest:view',
    'worksheets:view',
    'dataflow:view',
    'credentials:view',
  ]),
  color: 'gray',
}

/**
 * Billing Manager — finance contact who owns billing but should not
 * touch data or pipelines.
 */
export const BILLING_MANAGER_ROLE: BuiltInRoleDefinition = {
  id: 'billing',
  name: 'Billing Manager',
  description:
    'Manage subscription, invoices, and payment methods. No access to data, pipelines, or user management beyond seeing the billing contact list.',
  permissions: expandImplied([
    'billing:edit',
    'account:view',
    'users:view',
  ]),
  color: 'emerald',
}

/**
 * Pseudo-role used to describe SuperAdmin permissions in code. NOT
 * written to Firestore as an account-scoped role. The permissionsStore
 * uses this when a user has a doc in `/superAdmins/{uid}`.
 */
export const SUPER_ADMIN_ROLE: BuiltInRoleDefinition = {
  id: 'superAdmin',
  name: 'SuperAdmin',
  description:
    'Cross-account staff role. Holds every account-scoped permission plus SuperAdmin-only capabilities (cross-tenant access, encryption, SuperAdmin management). Granted exclusively via /superAdmins/{uid}.',
  permissions: ALL_PERMISSIONS,
  color: 'amber',
}

/** All roles that should be seeded into a new account. Order = display order. */
export const BUILT_IN_ROLES: BuiltInRoleDefinition[] = [
  ADMIN_ROLE,
  EDITOR_ROLE,
  VIEWER_ROLE,
  BILLING_MANAGER_ROLE,
]

export const BUILT_IN_ROLE_IDS = new Set(BUILT_IN_ROLES.map(r => r.id))

export function isBuiltInRoleId(id: string): boolean {
  return BUILT_IN_ROLE_IDS.has(id)
}

export function getBuiltInRole(id: string): BuiltInRoleDefinition | undefined {
  return BUILT_IN_ROLES.find(r => r.id === id)
}
